import { SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import { useTar } from '@/modules'
import { AllowedWeaponsPills, resolveUsedWeapon } from '@components/AllowedWeaponsPills'
import { RelativeDateTime } from '@components/datetime'
import { formatWorkTime } from '@/utils/time.utils'
import { ScoreRing } from './ScoreRing'
import type { CompletedChallenge } from './FeedbackDialog'

interface FeedbackRailProps {
  challenge: CompletedChallenge
  /** When true, surface the employer grade instead of the AI grade. */
  showEmployer: boolean
  /** Whether the bold Share Achievement CTA should be shown. */
  canShare: boolean
  onShareAchievement: () => void
  /** Whether the subtle "Evaluate the Feedback" action should be shown. */
  canEvaluate: boolean
  onEvaluate: () => void
}

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {label}
    </span>
    <span className="text-sm font-medium text-foreground">{children}</span>
  </div>
)

export const FeedbackRail = ({
  challenge,
  showEmployer,
  canShare,
  onShareAchievement,
  canEvaluate,
  onEvaluate,
}: FeedbackRailProps) => {
  const { t } = useTar()
  const { task } = challenge
  const { feedbackGrade, employerFeedback, workState, workTime } = challenge.challenge

  const displayGrade = showEmployer ? employerFeedback?.grade : feedbackGrade
  const scoreLabel = t(showEmployer ? 'challenges.employerScore' : 'challenges.yourScore').replace(
    /:$/,
    ''
  )
  // The backend only returns a grade when it qualifies as an achievement, so
  // presence alone gates the score ring (and the Share CTA in the dialog).
  const hasScore = displayGrade != null

  return (
    <aside className="flex w-full flex-col gap-5 md:w-[252px] md:shrink-0 md:min-h-0 md:overflow-y-auto custom-scrollbar md:pe-1">
      {hasScore && (
        <div className="flex shrink-0 justify-center rounded-2xl border border-border bg-muted/5 px-4 py-6">
          <ScoreRing grade={displayGrade} label={scoreLabel} />
        </div>
      )}

      <dl className="shrink-0 space-y-3 rounded-2xl border border-border bg-muted/5 p-4">
        <DetailRow label={t('share.publicPage.solvedWith')}>
          <AllowedWeaponsPills
            allowedWeapons={task.allowedWeapons}
            chosenWeapon={resolveUsedWeapon(workState?.chosenWeapon, task.allowedWeapons)}
          />
        </DetailRow>
        <DetailRow label={t('challenges.difficulty')}>{task.difficulty}</DetailRow>
        <DetailRow label={t('common.workTime')}>
          {formatWorkTime(workTime, t, { short: true })}
        </DetailRow>
        <DetailRow label={t('challenges.completed')}>
          <RelativeDateTime
            isoDateString={challenge.challenge.endTime}
            fallbackText={t('times.invalidDate')}
          />
        </DetailRow>
      </dl>

      {canShare && (
        <button
          type="button"
          onClick={onShareAchievement}
          className="group relative w-full shrink-0 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-success/10 p-4 text-start transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
        >
          <div className="flex items-start gap-2">
            <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t('challenges.shareAchievementTitle')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('challenges.shareAchievementSubtitle')}
              </p>
            </div>
          </div>
          <span className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-transform group-hover:scale-[1.02]">
            {t('challenges.shareAchievement')}
            <ArrowRightIcon className="h-4 w-4 transition-transform rtl:rotate-180 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
          </span>
        </button>
      )}

      {canEvaluate && (
        <button
          type="button"
          onClick={onEvaluate}
          className="shrink-0 text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          {t('userFeedback.evaluateTheFeedback')}
        </button>
      )}
    </aside>
  )
}
